import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { FiUsers, FiSearch, FiFilter, FiExternalLink, FiPlus, FiGlobe, FiCalendar, FiChevronRight } from 'react-icons/fi';

// Date formatting helper
function formatDisplayDate(value) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = String(date.getDate()).padStart(2, "0");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const weekdays = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  return `${day}-${months[date.getMonth()]}-${date.getFullYear()}, ${weekdays[date.getDay()]}`;
}

export default function AdminClients() {
  const { adminUser } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await adminApi.getClients(adminUser.admin_session_token);
        if (res.success) setClients(res.clients || []);
      } catch (e) {
        console.error('Error fetching clients:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, [adminUser]);

  const filtered = clients.filter(c => {
    const matchesSearch = `${c.client_name} ${c.business_name} ${c.website_domain}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) return (
    <div className="animate-pulse space-y-8">
      <div className="h-10 w-48 bg-slate-200 rounded-lg"></div>
      <div className="h-[500px] bg-surface rounded-xl"></div>
    </div>
  );

  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-ink tracking-tight">Client Portfolio</h1>
          <p className="text-sm text-muted">Manage partner access and configurations</p>
        </div>
        <button onClick={() => navigate('/admin/clients/new')} className="px-6 py-3 bg-gradient-to-r from-primary to-accent text-white rounded-xl font-medium flex items-center gap-2 shadow-card hover:-translate-y-0.5 transition-premium active:scale-95">
          <SafeIcon icon={FiPlus} />
          <span>New Client</span>
        </button>
      </header>

      <div className="bg-surface rounded-xl p-3 shadow-card border border-border flex flex-col lg:flex-row items-center gap-3">
        <div className="flex-grow relative group">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-primary transition-colors">
            <SafeIcon icon={FiSearch} />
          </div>
          <input 
            type="text" 
            value={searchTerm} 
            onChange={e => setSearchTerm(e.target.value)} 
            placeholder="Search clients..." 
            className="w-full pl-12 pr-4 py-3 bg-slate-50 rounded-lg text-sm font-medium outline-none border-2 border-transparent focus:border-primary/30 focus:ring-2 focus:ring-primary/10 transition-all" 
          />
        </div>
        <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-lg">
          {['All', 'Trial', 'Active', 'Grace', 'Expired'].map(s => (
            <button 
              key={s} 
              onClick={() => setStatusFilter(s)} 
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                statusFilter === s ? 'bg-white text-primary shadow-sm' : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-surface rounded-xl shadow-card border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-slate-50/50 border-b border-border">
                <th className="py-4 px-6 text-xs font-semibold text-muted uppercase tracking-wide">Business Partner</th>
                <th className="py-4 px-6 text-xs font-semibold text-muted uppercase tracking-wide">Domain</th>
                <th className="py-4 px-6 text-xs font-semibold text-muted uppercase tracking-wide">Status</th>
                <th className="py-4 px-6 text-xs font-semibold text-muted uppercase tracking-wide">Expiry</th>
                <th className="py-4 px-6 text-xs font-semibold text-muted uppercase tracking-wide text-center">Leads</th>
                <th className="py-4 px-6 text-xs font-semibold text-muted uppercase tracking-wide text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(client => (
                <tr key={client.client_key} className="group hover:bg-slate-50/50 transition-all cursor-pointer" onClick={() => navigate(`/admin/clients/${client.client_key}`)}>
                  <td className="py-4 px-6">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-ink">{client.business_name}</span>
                      <span className="text-xs text-muted mt-0.5">{client.client_name}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2 text-primary font-medium text-sm">
                      <SafeIcon icon={FiGlobe} />
                      {client.website_domain}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                      client.status === 'Active' ? 'bg-success/10 text-success' : 
                      client.status === 'Trial' ? 'bg-primary/10 text-primary' : 
                      client.status === 'Grace' ? 'bg-warning/10 text-warning' : 
                      'bg-error/10 text-error'
                    }`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-ink">
                        {client.service_end_date ? formatDisplayDate(client.service_end_date) : 'Ongoing'}
                      </span>
                      {client.days_remaining !== undefined && (
                        <span className={`text-xs font-medium mt-0.5 ${
                          client.days_remaining < 5 ? 'text-error' : 'text-muted'
                        }`}>
                          {client.days_remaining} days left
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <span className="text-sm font-semibold text-ink">{client.total_leads || 0}</span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <button className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 group-hover:bg-primary group-hover:text-white text-slate-700 rounded-lg text-xs font-medium transition-all">
                      <SafeIcon icon={FiExternalLink} />
                      <span>Configure</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="py-16 text-center">
            <div className="w-12 h-12 bg-slate-50 rounded-lg flex items-center justify-center mx-auto mb-4 border border-border">
              <SafeIcon icon={FiUsers} className="text-xl text-slate-300" />
            </div>
            <h4 className="text-lg font-semibold text-ink mb-2">No Clients Found</h4>
            <p className="text-muted text-sm">Refine your search or filter criteria</p>
          </div>
        )}
      </div>
    </div>
  );
}